import { useCallback } from "react";
import { useVaultRecord } from "@/features/vault/use-vault-record";
import {
  emptySeasonVault,
  type SavedRaceTeam,
  type SeasonTeamVault,
} from "../lib/my-team";

const NAMESPACE = "f1-my-team";

/**
 * The signed-in user's saved fantasy team for a season, stored locally in
 * the browser vault (IndexedDB). One record per (user, season) holding a
 * snapshot per race, so the line-up carries forward race to race and the
 * transfer allowance can be resolved from history.
 */
export function useMyTeamVault(season: number | undefined) {
  const record = useVaultRecord<SeasonTeamVault>(
    NAMESPACE,
    season !== undefined ? String(season) : null,
  );

  const { value, save } = record;
  const vault = value ?? null;

  const saveRaceTeam = useCallback(
    (snapshot: SavedRaceTeam) => {
      const base = value ?? emptySeasonVault();
      save({
        ...base,
        races: { ...base.races, [snapshot.raceSlug]: snapshot },
      });
    },
    [value, save],
  );

  const removeRaceTeam = useCallback(
    (raceSlug: string) => {
      if (!value?.races[raceSlug]) return;
      const races = { ...value.races };
      delete races[raceSlug];
      save({ ...value, races });
    },
    [value, save],
  );

  return {
    signedIn: record.signedIn,
    isLoaded: record.isLoaded,
    vault,
    saveRaceTeam,
    removeRaceTeam,
  };
}
